import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { Badge } from "@/components/StatusBadge";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import type { DataTableColumn } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type {
  ApplyGenerateFromResource,
  ImportBatchListItemResource,
  Page,
  RollbackImportBatchResource,
} from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { formatDateCell, todayAsDateInputValue } from "@/lib/format";

/**
 * Settings → Import batches (Story 8.4 — no such screen existed before
 * this story; the wizard only ever created batches, never listed them).
 * `role=Role.OWNER` gates both `GET /import-batches` and `POST /import-
 * batches/{id}/rollback` on the server — this screen withholds itself
 * entirely for anyone but a confirmed Owner, the same absence-based
 * pattern `ImportWizard.tsx`/`UsersList.tsx` already established (AD-14:
 * "the frontend mirrors route role declarations to decide what to render
 * and enforces nothing").
 *
 * The rollback action reuses `ConfirmDialog` (Story 2.6's own established
 * irreversible-action pattern) — its description states the real row
 * count and the real 30-day restorability window (VC-8.4-06), plus the
 * "a hand-edited row is still removed; use record-by-record undo to keep
 * it" disclosure the AC's own third bullet requires, verbatim, before the
 * rollback runs (VC-8.4-07).
 */
export function ImportBatches() {
  const isOwner = useIsOwner();
  const queryClient = useQueryClient();
  const [rollbackTarget, setRollbackTarget] = useState<ImportBatchListItemResource | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | undefined>(undefined);
  const [generateFromTarget, setGenerateFromTarget] = useState<ImportBatchListItemResource | null>(null);
  const [generateFromDate, setGenerateFromDate] = useState(todayAsDateInputValue);
  const [isApplyingGenerateFrom, setIsApplyingGenerateFrom] = useState(false);
  const [generateFromError, setGenerateFromError] = useState<string | undefined>(undefined);
  const [generateFromResult, setGenerateFromResult] = useState<ApplyGenerateFromResource | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = "Import batches — PropDesk";
  }, []);

  const listQueryKey = ["import-batches", "list"] as const;
  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<ImportBatchListItemResource>>("/import-batches"),
    enabled: isOwner,
  });

  if (!isOwner) {
    return (
      <div className="max-w-record-column-max">
        <ReadOnlyNotice className="mb-4" />
        <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Import batches</h1>
        <p className="mt-4 text-body text-ink-secondary">Only the account owner can view past imports.</p>
      </div>
    );
  }

  async function confirmRollback() {
    if (rollbackTarget === null) {
      return;
    }
    setIsRollingBack(true);
    setRollbackError(undefined);
    try {
      await apiFetch<RollbackImportBatchResource>(`/import-batches/${rollbackTarget.id}/rollback`, {
        method: "POST",
      });
      await queryClient.invalidateQueries({ queryKey: listQueryKey });
      setRollbackTarget(null);
    } catch (error) {
      setRollbackError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRollingBack(false);
    }
  }

  function openGenerateFromDialog(row: ImportBatchListItemResource) {
    setGenerateFromTarget(row);
    setGenerateFromDate(todayAsDateInputValue());
    setGenerateFromError(undefined);
    setGenerateFromResult(null);
  }

  async function confirmGenerateFrom() {
    if (generateFromTarget === null) {
      return;
    }
    setIsApplyingGenerateFrom(true);
    setGenerateFromError(undefined);
    try {
      const result = await apiFetch<ApplyGenerateFromResource>(
        `/import-batches/${generateFromTarget.id}/generate-from`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ generate_from: generateFromDate }),
        },
      );
      setGenerateFromResult(result);
    } catch (error) {
      setGenerateFromError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsApplyingGenerateFrom(false);
    }
  }

  const columns: DataTableColumn<ImportBatchListItemResource>[] = [
    { key: "label" },
    { key: "run_at", tabularFigures: true, render: (row) => formatDateCell(row.run_at) },
    { key: "row_count", align: "right", tabularFigures: true },
    {
      key: "already_rolled_back",
      sortable: false,
      render: (row) =>
        row.already_rolled_back ? (
          <Badge label="Rolled back" fill="neutral" />
        ) : (
          <Badge label="Live" fill="settled" />
        ),
    },
  ];

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <h1 ref={headingRef} tabIndex={-1} className="text-title font-semibold tracking-title text-ink md:hidden">
        Import batches
      </h1>
      <p className="mb-6 mt-2 text-body text-ink-secondary">
        Every import that has been run, most recent first. Rolling one back removes every record it created.
      </p>
      <p className="mb-6 text-meta text-ink-secondary">
        Ran a new import? <Link to="/settings/import" className="underline">Go to Import</Link>.
      </p>
      <DataTable
        caption="Import batches"
        itemNamePlural="batches"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        emptyState={{
          heading: "No imports yet.",
          body: "Once you run an import from Settings → Import, it shows up here.",
        }}
        rowActions={(row: ImportBatchListItemResource) =>
          row.already_rolled_back ? null : (
            <>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Set generate-from date for ${row.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  openGenerateFromDialog(row);
                }}
              >
                Set generate-from date
              </Button>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Roll back ${row.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setRollbackTarget(row);
                }}
              >
                Roll back
              </Button>
            </>
          )
        }
      />
      {generateFromTarget ? (
        <Dialog
          open={generateFromTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setGenerateFromTarget(null);
              setGenerateFromError(undefined);
              setGenerateFromResult(null);
            }
          }}
          title={`Start generating charges from a date — ${generateFromTarget.label}`}
          description={
            'Every lease in this batch that has not already been given a date will start generating ' +
            "charges from the date you choose. A lease that already has one keeps it — this never " +
            "overwrites an earlier date, even a different one."
          }
          fallbackFocusRef={headingRef}
        >
          {generateFromResult ? (
            <div className="mt-6 flex flex-col gap-4">
              <p className="text-body text-ink">
                {generateFromResult.leases_updated} lease{generateFromResult.leases_updated === 1 ? "" : "s"}{" "}
                will start generating charges from {generateFromResult.generate_from}.
              </p>
              <div className="flex justify-end">
                <Button type="button" onClick={() => setGenerateFromTarget(null)}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              <label className="flex flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">Start generating charges from</span>
                <Input
                  type="date"
                  value={generateFromDate}
                  onChange={(event) => setGenerateFromDate(event.target.value)}
                />
              </label>
              {generateFromError ? (
                <p role="alert" className="text-meta text-danger">
                  {generateFromError}
                </p>
              ) : null}
              <div className="flex flex-col-reverse gap-3 md:flex-row md:justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setGenerateFromTarget(null)}
                  disabled={isApplyingGenerateFrom}
                  className="w-full md:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => void confirmGenerateFrom()}
                  disabled={isApplyingGenerateFrom || generateFromDate === ""}
                  className="w-full md:w-auto"
                >
                  {isApplyingGenerateFrom ? "Applying…" : "Apply"}
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      ) : null}
      {rollbackTarget ? (
        <ConfirmDialog
          open={rollbackTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRollbackTarget(null);
              setRollbackError(undefined);
            }
          }}
          title="Roll back this import?"
          description={
            `Rolling back "${rollbackTarget.label}" removes ${rollbackTarget.row_count} ` +
            `record${rollbackTarget.row_count === 1 ? "" : "s"} it created, right away. Removed records stay ` +
            `restorable for 30 days. If you've hand-edited any of them since, the edit won't save it — this ` +
            `removes it anyway. Use record-by-record undo instead for anything you want to keep.`
          }
          confirmLabel={`Roll back ${rollbackTarget.label}`}
          confirmingLabel="Rolling back…"
          isConfirming={isRollingBack}
          onConfirm={() => void confirmRollback()}
          fallbackFocusRef={headingRef}
          {...(rollbackError ? { errorMessage: rollbackError } : {})}
        />
      ) : null}
    </div>
  );
}
